/**
 * WidgetThumbnail - Aperçu miniature des widgets avec données mock
 * 
 * Utilise IntersectionObserver pour le lazy rendering (virtualisation).
 * Affiche un aperçu réaliste de chaque type de widget.
 */

import React, { useRef, useState, useEffect, useMemo } from "react";
import type {
  WidgetType,
  WidgetConfig,
  WidgetData,
  ChartData,
  KpiData,
  TableData,
  DocumentsData,
  ChartVisualConfig,
} from "@/models/widget-v2";
import { WIDGET_TYPES, createDefaultWidgetConfig } from "@/models/widget-v2";
import WidgetRenderer from "./WidgetRenderer";
import { FaFileAlt, FaClock, FaUser } from "react-icons/fa";
import useTranslation from "@/hooks/use-translation";
import { chartColors, vibrantColors } from "@/styles/colors";

// ============================================================================
// Types
// ============================================================================

interface WidgetThumbnailProps {
  type: WidgetType;
  className?: string;
}

// ============================================================================
// Mock Data Generators
// ============================================================================

/**
 * Génère des données mock pour les graphiques temporels (line, bar, area)
 */
function generateTemporalChartData(): ChartData {
  return {
    labels: ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"],
    datasets: [
      {
        label: "Données",
        data: [42, 58, 35, 78, 62, 45, 89],
      },
    ],
  };
}

/**
 * Génère des données mock pour les graphiques catégoriels (pie, donut)
 */
function generateCategoryChartData(): ChartData {
  return {
    labels: ["Twitter", "Facebook", "Instagram", "TikTok"],
    datasets: [
      {
        label: "Répartition",
        data: [35, 28, 22, 15],
      },
    ],
  };
}

/**
 * Génère des données mock pour KPI
 */
function generateKpiData(): KpiData {
  return {
    value: 12847,
    change: {
      value: 1250,
      percentage: 10.8,
      trend: "up",
    },
  };
}

/**
 * Génère des données mock pour Gauge
 */
function generateGaugeData(): KpiData {
  return {
    value: 73,
  };
}

/**
 * Génère des données mock pour Table
 */
function generateTableData(): TableData {
  return {
    columns: [
      { key: "name", label: "Nom" },
      { key: "value", label: "Valeur" },
      { key: "trend", label: "Tendance" },
    ],
    rows: [
      { name: "Twitter", value: 1250, trend: "+12%" },
      { name: "Facebook", value: 890, trend: "+5%" },
      { name: "Instagram", value: 654, trend: "-3%" },
    ],
  };
}

/**
 * Génère des données mock pour Documents
 * Structure conforme au type Content pour un rendu correct dans CardView
 */
function generateDocumentsData(): DocumentsData {
  return {
    documents: [
      { 
        id: "1", 
        normalized: {
          title: "Analyse du marché tech",
          text: "Les tendances actuelles montrent une forte croissance dans le secteur de l'IA générative...",
          date: "2024-01-15",
          author: {
            username: "TechAnalyst",
            text: "",
          },
          language: "fr",
        },
      },
      { 
        id: "2", 
        normalized: {
          title: "Rapport trimestriel",
          text: "Les résultats du Q4 dépassent les attentes avec une augmentation de 15% du chiffre d'affaires...",
          date: "2024-01-14",
          author: {
            username: "FinanceTeam",
            text: "",
          },
          language: "fr",
        },
      },
    ] as any,
    total: 2,
  };
}

/**
 * Génère des données mock pour le widget Graph
 * Crée un petit réseau de documents avec des relations pour un aperçu visuel
 */
function generateGraphData(): DocumentsData {
  return {
    documents: [
      { 
        id: "post-1", 
        normalized: { title: "Post", username: "User1" },
        author: { id: "author-1", normalized: { username: "Alice" } },
        reply_to: { id: "post-2" },
      },
      { 
        id: "post-2", 
        normalized: { title: "Original", username: "User2" },
        author: { id: "author-2", normalized: { username: "Bob" } },
      },
      { 
        id: "post-3", 
        normalized: { title: "Reply", username: "User3" },
        author: { id: "author-1" },
        reply_to: { id: "post-1" },
      },
    ] as any,
    total: 3,
  };
}

/**
 * Génère des données mock pour la carte géographique
 */
function generateMapData(): DocumentsData {
  return {
    documents: [
      { country: "France", latitude: 46.603354, longitude: 1.888334, value: 250, color: chartColors.blue },
      { country: "USA", latitude: 37.09024, longitude: -95.712891, value: 180, color: chartColors.green },
      { country: "Japon", latitude: 36.204824, longitude: 138.252924, value: 120, color: vibrantColors[0] },
      { country: "Brésil", latitude: -14.235004, longitude: -51.92528, value: 90, color: chartColors.red },
    ],
    total: 4,
  };
}

/**
 * Génère des données mock pour Heatmap
 */
function generateHeatmapData(): ChartData {
  return {
    labels: ["Lun", "Mar", "Mer", "Jeu", "Ven"],
    datasets: [
      { label: "00h", data: [10, 20, 30, 40, 50] },
      { label: "06h", data: [25, 35, 45, 55, 65] },
      { label: "12h", data: [40, 50, 60, 70, 80] },
      { label: "18h", data: [30, 40, 50, 60, 70] },
    ],
  };
}

/**
 * Génère des données mock pour Treemap
 * Format hiérarchique avec des proportions variées pour un rendu visuel pertinent
 */
function generateTreemapData(): ChartData {
  return {
    labels: [
      "Marketing",
      "Développement", 
      "Design",
      "Ventes",
      "Support",
      "RH",
      "Finance",
      "Juridique",
    ],
    datasets: [
      {
        label: "Budget",
        data: [85, 72, 45, 38, 28, 22, 18, 12],
      },
    ],
  };
}

/**
 * Génère des données mock pour Scatter
 * Note: ApexCharts scatter nécessite des données au format [[x,y], ...]
 */
function generateScatterData(): ChartData {
  return {
    labels: ["10", "25", "35", "50", "65"],
    datasets: [
      {
        label: "Points",
        data: [20, 40, 30, 60, 45],
      },
    ],
  };
}

/**
 * Génère des données mock pour Wordcloud
 */
function generateWordcloudData(): ChartData {
  return {
    labels: ["Innovation", "Tech", "Data", "IA", "Cloud", "Mobile"],
    datasets: [
      {
        label: "Fréquence",
        data: [80, 65, 55, 45, 35, 25],
      },
    ],
  };
}

/**
 * Génère les données mock appropriées selon le type de widget
 */
function generateMockData(type: WidgetType): WidgetData {
  switch (type) {
    case WIDGET_TYPES.LINE:
    case WIDGET_TYPES.BAR:
    case WIDGET_TYPES.AREA:
      return generateTemporalChartData();

    case WIDGET_TYPES.PIE:
      // PIE et DONUT partagent le même type (pie_widget)
      return generateCategoryChartData();

    case WIDGET_TYPES.KPI:
      return generateKpiData();

    case WIDGET_TYPES.GAUGE:
      return generateGaugeData();

    case WIDGET_TYPES.TABLE:
      return generateTableData();

    case WIDGET_TYPES.DOCUMENTS:
      return generateDocumentsData();

    case WIDGET_TYPES.GRAPH:
      return generateGraphData();

    case WIDGET_TYPES.MAP:
      return generateMapData();

    case WIDGET_TYPES.HEATMAP:
      return generateHeatmapData();

    case WIDGET_TYPES.TREEMAP:
      return generateTreemapData();

    case WIDGET_TYPES.SCATTER:
      return generateScatterData();

    case WIDGET_TYPES.WORDCLOUD:
      return generateWordcloudData();

    case WIDGET_TYPES.TEXT:
    default:
      return null;
  }
}

/**
 * Crée une configuration optimisée pour les thumbnails (sans titre, sans légende, sans animation)
 */
function createThumbnailConfig(type: WidgetType): WidgetConfig {
  const baseConfig = createDefaultWidgetConfig(type, `thumbnail-${type}`);
  
  // Configuration visuelle optimisée pour les miniatures
  const thumbnailVisual: ChartVisualConfig = {
    ...baseConfig.visual,
    title: undefined,
    subtitle: undefined,
    showLegend: false,
    animation: false,
    showDataLabels: false,
    showGrid: false,
  };
  
  return {
    ...baseConfig,
    visual: thumbnailVisual,
  };
}

// ============================================================================
// Skeleton Component
// ============================================================================

const WidgetThumbnailSkeleton: React.FC = () => (
  <div className="h-full w-full flex items-center justify-center bg-arc-surface-secondary animate-pulse">
    <div className="w-8 h-8 rounded-lg bg-arc-surface-tertiary" />
  </div>
);

// ============================================================================
// Documents Thumbnail Component (vue simplifiée pour miniature)
// ============================================================================

interface DocumentThumbnailItem {
  id: string;
  normalized: {
    title?: string;
    text: string;
    date?: string;
    author?: {
      username?: string;
      text: string;
    };
  };
}

const DocumentsThumbnailView: React.FC<{ data: DocumentsData }> = ({ data }) => {
  const { t } = useTranslation();
  const documents = (data.documents || []) as unknown as DocumentThumbnailItem[];
  
  return (
    <div className="h-full w-full flex flex-col bg-arc-surface">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-arc-border bg-arc-surface-secondary shrink-0">
        <FaFileAlt className="w-3 h-3 text-content-tertiary" />
        <span className="text-[10px] text-content-tertiary">
          {t("n_documents", { count: data.total })}
        </span>
      </div>
      
      {/* Documents list */}
      <div className="flex-1 overflow-hidden p-2 space-y-2">
        {documents.slice(0, 3).map((doc) => (
          <div 
            key={doc.id}
            className="p-2 rounded-md bg-arc-surface-secondary border border-arc-border"
          >
            <div className="font-medium text-[11px] text-content-primary truncate mb-1">
              {doc.normalized?.title || "Sans titre"}
            </div>
            <div className="text-[9px] text-content-secondary line-clamp-2 mb-1">
              {doc.normalized?.text || ""}
            </div>
            <div className="flex items-center gap-2 text-[8px] text-content-tertiary">
              {doc.normalized?.author?.username && (
                <span className="flex items-center gap-0.5">
                  <FaUser className="w-2 h-2" />
                  {doc.normalized.author.username}
                </span>
              )}
              {doc.normalized?.date && (
                <span className="flex items-center gap-0.5">
                  <FaClock className="w-2 h-2" />
                  {new Date(doc.normalized.date).toLocaleDateString("fr-FR", { 
                    day: "numeric", 
                    month: "short" 
                  })}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// Constants
// ============================================================================

// Taille de rendu interne (avant scale)
const RENDER_WIDTH = 280;
const RENDER_HEIGHT = 200;

// Scale factor pour réduire le widget
const SCALE_FACTOR = 0.42;

// ============================================================================
// Main Component
// ============================================================================

export const WidgetThumbnail: React.FC<WidgetThumbnailProps> = ({
  type,
  className = "",
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  // Lazy rendering avec IntersectionObserver
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "50px" }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Générer les données et config mock (mémorisés)
  const mockData = useMemo(() => generateMockData(type), [type]);
  const mockConfig = useMemo(() => createThumbnailConfig(type), [type]);

  // Dimensions finales après scale
  const finalHeight = RENDER_HEIGHT * SCALE_FACTOR;

  // Rendu spécial pour Documents (vue simplifiée)
  const renderContent = () => {
    if (type === WIDGET_TYPES.DOCUMENTS) {
      return <DocumentsThumbnailView data={mockData as DocumentsData} />;
    }
    return <WidgetRenderer config={mockConfig} data={mockData} />;
  };

  return (
    <div
      ref={containerRef}
      className={`relative overflow-hidden pointer-events-none ${className}`}
      style={{
        width: "100%",
        height: finalHeight,
      }}
    >
      {isVisible ? (
        <div
          className="absolute top-1/2 left-1/2"
          style={{
            width: RENDER_WIDTH,
            height: RENDER_HEIGHT,
            transform: `translate(-50%, -50%) scale(${SCALE_FACTOR})`,
            transformOrigin: "center center",
          }}
        >
          {renderContent()}
        </div>
      ) : (
        <WidgetThumbnailSkeleton />
      )}
    </div>
  );
};

export default WidgetThumbnail;
